import { isEqual, omit } from "lodash";

/**
 * Fields the Character/WorldbuildingEntry parent-sync effects are allowed to
 * overwrite on local state while the user has unsaved edits (localDirty).
 * Everything else is user-editable content that a save response must never replace.
 */
export const PARENT_SYNCED_METADATA_FIELDS = ["updatedAt", "collapsed", "displayOrder", "aiVisibility"];

type EntityLike = Record<string, unknown> | null | undefined;

type UpdatedAt = string | number | Date | null | undefined;
type VersionedEntity = { id: string; updatedAt?: UpdatedAt };

const timestampMillis = (value: UpdatedAt): number | null => {
  const millis = value instanceof Date
    ? value.getTime()
    : typeof value === "string" || typeof value === "number"
      ? new Date(value).getTime()
      : Number.NaN;
  return Number.isFinite(millis) ? millis : null;
};

/**
 * Copy only fresh server timestamps into the latest local entities. Bulk metadata
 * requests can finish after an ordinary autosave, so an older response must not
 * downgrade the optimistic-lock version or replace locally edited content.
 */
export const mergeSavedUpdatedAts = <T extends VersionedEntity>(
  currentEntities: T[],
  savedEntities: VersionedEntity[],
): T[] => {
  const savedById = new Map<string, { updatedAt: UpdatedAt; millis: number }>();
  savedEntities.forEach((saved) => {
    const millis = timestampMillis(saved.updatedAt);
    const previous = savedById.get(saved.id);
    if (millis !== null && (!previous || millis > previous.millis)) {
      savedById.set(saved.id, { updatedAt: saved.updatedAt, millis });
    }
  });

  let changed = false;
  const merged = currentEntities.map((current) => {
    const saved = savedById.get(current.id);
    if (!saved) return current;

    const currentMillis = timestampMillis(current.updatedAt);
    if (currentMillis !== null && currentMillis >= saved.millis) return current;

    changed = true;
    return { ...current, updatedAt: saved.updatedAt };
  });

  return changed ? merged : currentEntities;
};

/**
 * True when `current` has content edits that are not in `reference` — i.e. the user
 * kept editing after an autosave request captured `reference` (or the server copy in
 * `reference` lacks the local content). Metadata fields are ignored because they are
 * reconciled from the server response rather than typed by the user, and would
 * otherwise make every save response look like a conflicting edit.
 */
export const hasUnsavedEditsSince = (current: EntityLike, reference: EntityLike): boolean =>
  !isEqual(omit(current ?? {}, PARENT_SYNCED_METADATA_FIELDS), omit(reference ?? {}, PARENT_SYNCED_METADATA_FIELDS));


type ContentEntity = Record<string, any>;

/** Keep post-submission edits, using the server record for every untouched field.
 * Rich text values are atomic. Custom fields are matched by ID, not array index.
 */
export const reconcileEntityContent = <T extends ContentEntity>(submitted: T, current: T, server: T): T => {
  const recovered: ContentEntity = { ...server };
  for (const key of new Set([...Object.keys(submitted), ...Object.keys(current)])) {
    if (PARENT_SYNCED_METADATA_FIELDS.includes(key) || isEqual(current[key], submitted[key])) continue;
    if (key !== "customFields") {
      recovered[key] = current[key];
      continue;
    }
    const before: ContentEntity[] = submitted.customFields ?? [];
    const local: ContentEntity[] = current.customFields ?? [];
    const remote: ContentEntity[] = server.customFields ?? [];
    const beforeById = new Map(before.map((field) => [field.id, field]));
    const localById = new Map(local.map((field) => [field.id, field]));
    const merged = new Map(remote.map((field) => [field.id, field]));
    for (const field of before) {
      if (!localById.has(field.id)) merged.delete(field.id);
    }
    for (const field of local) {
      const previous = beforeById.get(field.id);
      if (isEqual(field, previous)) continue;
      const authoritative = merged.get(field.id);
      // An intentional edit can restore a deleted field. An untouched field cannot.
      const changedProperties = previous
        ? Object.fromEntries([...new Set([...Object.keys(previous), ...Object.keys(field)])].filter((key) => !isEqual(field[key], previous[key])).map((key) => [key, field[key]]))
        : field;
      merged.set(field.id, authoritative ? { ...authoritative, ...changedProperties } : field);
    }
    const reordered = local.some((field) => !beforeById.has(field.id)) || !isEqual(
      before.filter((field) => localById.has(field.id)).map((field) => field.id),
      local.filter((field) => beforeById.has(field.id)).map((field) => field.id),
    );
    const order = reordered ? [...local, ...remote] : [...remote, ...local];
    recovered.customFields = [...new Set(order.map((field) => field.id))].filter((id) => merged.has(id)).map((id) => merged.get(id));
  }
  return recovered as T;
};
